import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Avatar,
  Typography,
  IconButton,
} from "@mui/material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";

const priorities = ["low", "medium", "high", "urgent"];

export default function ReminderForm({
  contactId,
  onSave,
  onCancel,
  reminder = null,
}) {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const getLocalDateTimeString = (date = new Date()) => {
    const offsetMs = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offsetMs);
    return localDate.toISOString().slice(0, 16);
  };
  const [formData, setFormData] = useState({
    title: "",
    notes: "",
    date: getLocalDateTimeString(),
    type: "",
    priority: "medium",
    assigned_to: "",
  });
  const { domain, token } = useSelector((state) => state.tokenReducer);

  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [defaultActivityTypeId, setDefaultActivityTypeId] = useState("");

  const formattedDateTime =
    reminder?.formatted_date && reminder?.formatted_time
      ? `${reminder.formatted_date}T${reminder.formatted_time.slice(0, 5)}`
      : null;
  useEffect(() => {
    if (reminder) {
      setFormData({
        title: reminder.title || "",
        notes: reminder.notes || "",
        // date: reminder.date?.slice(0, 16) || getLocalDateTimeString(),
        date: formattedDateTime || getLocalDateTimeString(),
        type: reminder.reminder_name || "follow_up",
        priority: reminder.priority || "medium",
        assigned_to: reminder.assigned_to || "",
      });
    }
  }, [formattedDateTime, reminder]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(
          `${domain}crm/crm-members/active/?company_id=${requestCompanyId}`,
          {
            headers: {
              Authorization: `token ${token}`,
            },
          }
        );
        console.log(res);
        setUsers(res.data);
      } catch (error) {
        console.error(
          "Failed to fetch users:",
          error?.response?.data || error.message
        );
      }
    };

    fetchUsers();
  }, [domain, token, requestCompanyId]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const validate = () => {
    const errs = {};
    if (!formData.title) errs.title = "Title is required";
    if (!formData.date) errs.date = "Date is required";
    if (!formData.assigned_to) errs.assigned_to = "Agent is required";
    return errs;
  };

  useEffect(() => {
    const fetchReminderTypes = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/reminder-type/`);
        const typeList = res.responseDetails?.results || res.responseDetails || [];
        const activity = await customGetAPI(`${domain}crm/activity-type/`);
        const activitytypeList = activity.responseDetails?.results || [];
        setTypes(typeList);

        if (activitytypeList.length > 0) {
          const noteType = activitytypeList.find(
            (t) => t.label?.toLowerCase() === "reminder"
          );
          const defaultTypeId = noteType ? noteType.id : activitytypeList[0].id;
          setDefaultActivityTypeId(defaultTypeId);
        }
        if (!reminder) {
          if (typeList.length > 0) {
            const followUpType = typeList.find(
              (t) => t.label?.toLowerCase() === "follow up"
            );
            setFormData((prev) => ({
              ...prev,
              type: followUpType ? followUpType.id : typeList[0].id,
            }));
          }
        } else {
          const matchedType = typeList.find(
            (t) =>
              t.label?.toLowerCase() === reminder.reminder_name?.toLowerCase()
          );
          setFormData((prev) => ({
            ...prev,
            type: matchedType ? matchedType.id : typeList[0]?.id || "",
          }));
        }
      } catch (err) {
        console.error("Failed to fetch reminder types:", err);
      }
    };
    fetchReminderTypes();
  }, [domain, reminder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) return setErrors(errs);

    setSaving(true);
    try {
      if (reminder?.id) {
        // EDIT
        await axios.patch(`${domain}crm/reminders/${reminder.id}/`, {
          ...formData,
          date: new Date(formData.date).toISOString(),
        }, {
          headers: { Authorization: `token ${token}` },
        });
      } else {
        await axios.post(
          `${domain}crm/reminders/`,
          {
            ...formData,
            date: new Date(formData.date).toISOString(),
            contact: contactId,
          },
          {
            headers: {
              Authorization: `token ${token}`,
            },
          }
        );
        const activityPayload = {
          type: defaultActivityTypeId,
          title: `Reminder: ${formData.title}`,
          description: formData.notes || "Reminder set",
          date: getLocalDateTimeString(),
          contact: contactId,
        };
        await axios.post(`${domain}crm/activities/`, activityPayload, {
          headers: {
            Authorization: `token ${token}`,
          },
        });
      }
      onSave?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        overflowY: "auto",
      },
    },
  };

  return (
    // <Dialog open={true} onClose={onCancel} fullWidth maxWidth="sm">
    <Dialog
      open
      onClose={() => {}}
      disableEscapeKeyDown
      onBackdropClick={() => {}} // Prevent clicking outside to close
    >
      {/* <DialogHeader> */}
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {reminder ? "Edit Reminder" : "Set Reminder"}
          </Typography>
          <IconButton onClick={onCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      {/* </DialogHeader> */}
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={handleChange("title")}
            margin="normal"
            error={!!errors.title}
            helperText={errors.title}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="reminder-type-label">Reminder Type</InputLabel>
            <Select
              labelId="reminder-type-label"
              value={formData.type}
              label="Reminder Type"
              onChange={handleChange("type")}
            >
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel shrink>Priority</InputLabel>
            <Select
              value={formData.priority}
              label="Priority"
              onChange={handleChange("priority")}
            >
              {priorities.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="datetime-local"
            label="Reminder Date"
            value={formData.date}
            onChange={handleChange("date")}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            error={!!errors.date}
            helperText={errors.date}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="assign-to-label">Assign To</InputLabel>
            <Select
              labelId="assign-to-label"
              value={formData.assigned_to}
              onChange={handleChange("assigned_to")}
              label="Assign To"
              size="medium"
              MenuProps={menuProps}
              renderValue={(selected) => {
                const selectedUser = users.find((u) => u.id === selected);
                return selectedUser ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      src={selectedUser.image_url}
                      sx={{ width: 20, height: 20, fontSize: 12 }}
                    >
                      {selectedUser.full_name?.[0] ||
                        selectedUser.username?.[0]}
                    </Avatar>
                    <Typography variant="body2" noWrap>
                      {selectedUser.full_name || selectedUser.username}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Select Agent
                  </Typography>
                );
              }}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      src={user.image_url}
                      sx={{ width: 24, height: 24, fontSize: 12 }}
                    >
                      {user.full_name?.[0] || user.username?.[0]}
                    </Avatar>
                    <Typography variant="body2" noWrap>
                      {user.full_name || user.username}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Notes"
            multiline
            minRows={3}
            value={formData.notes}
            onChange={handleChange("notes")}
            margin="normal"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "1rem",
              marginTop: 16,
            }}
          >
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? (
                <CircularProgress size={24} />
              ) : reminder ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
